import { forwardRef, useEffect } from 'react';
import { Group, Object3D } from 'three';

import { GroupProps, useThree } from '@react-three/fiber';
import { useEventCallback, useForkRef } from '@spazious/react-hooks';

import { useSelectPointer } from '../../hooks/index.js';
import { SelectBoxEvent, useSelectBox } from '../SelectBox/index.js';
import { useSelection } from '../Selection/index.js';

const HOLD_TIME_THRESHOLD = 100; // milliseconds

export interface SelectControlsProps extends GroupProps {
  /**
   * Whether to enable the selection or not
   */
  enabled?: boolean;
  /**
   * Active mode, if true, the selection will be appended to the current selection
   */
  shift?: boolean;
  /**
   * Whether to enable the box selection or not
   */
  box?: boolean;
  /**
   * Custom CSS border: default: '1px solid #55aaff'
   */
  border?: string;
  /**
   * Custom CSS background color, default: 'rgba(75, 160, 255, 0.1)'
   */
  backgroundColor?: string;
  /**
   * Callback for range selection changes
   */
  onSelectRange?: (evt: SelectBoxEvent) => void;

  /**
   * selection reducer for side effects
   */
  reducer?: (selected: Object3D[], items: Object3D[], shift: boolean) => Object3D[];
}

const defaultReducer = (selected: Object3D[], items: Object3D[], shift: boolean) =>
  shift && items.length > 0 ? Array.from(new Set<Object3D>([...selected, ...items])) : items;

export const SelectControls = forwardRef<Group, SelectControlsProps>(
  (
    {
      enabled,
      shift: shiftProp,
      box = true,
      children,
      onSelectRange,
      reducer = defaultReducer,
      ...restProps
    },
    forwardedRef
  ) => {
    const { invalidate } = useThree();
    const { selected, select, layers } = useSelection();

    useEffect(() => {
      // re-render threejs scene when selection changes
      invalidate(1000);
    }, [selected, invalidate]);

    const dispatch = useEventCallback(
      (selected: Object3D[], objects: Object3D[], shift: boolean) => {
        // TODO this should be passed to the raycaster to avoid unnecessary traverses
        const filteredObjects = layers
          ? objects.filter((item) => (item as any).isInstance && item.layers.test(layers))
          : objects;
        const items = reducer(selected, filteredObjects, shiftProp ? shiftProp : shift);
        select(items);
      }
    );

    const pointerProps = useSelectPointer(
      ({ object, shift, holdTime }) => {
        if (object && selected.includes(object) && holdTime > HOLD_TIME_THRESHOLD) {
          // object was acted upon after it is selected
          // hence no need to change selection
          return;
        }
        const objects = object ? [object] : [];
        dispatch(selected, objects, shift);
      },
      { enabled }
    );

    const groupRef = useSelectBox<Group>({
      enabled: box && enabled,
      onSelect: (evt) => {
        onSelectRange?.(evt);
        if (evt.phase === 'tick') {
          dispatch(selected, evt.selected, false);
        }
      },
    });

    return (
      <group ref={useForkRef(groupRef, forwardedRef)} {...pointerProps} {...restProps}>
        {children}
      </group>
    );
  }
);

export default SelectControls;
